/**
 * apps/battle/client/src/terminal/screens/QuestionScreen.tsx
 * Terminal-mode question loop. Handles both cluster (regular) and portal
 * battles via the `mode` prop. State machine inside this single screen:
 *
 *   loading-question --> awaiting-answer --(submit)--> awaiting-grade
 *                                                          |
 *                                          +---------------+----------------+
 *                                          v                                v
 *                                  feedback:correct                 feedback:wrong
 *                                          |                                |
 *                                          v                          attempts left? -> awaiting-answer (retry pinned)
 *                                          v                                |
 *                                   onAdvance() / fetch next            no -> feedback:exhausted -> onAdvance()
 *
 * After every submit, the parent is asked to refresh the /api/map snapshot,
 * which is how the cluster-exit narrative triggers.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../../api.js';
import type { Question, SubmitResp, NextResp, PortalNextResp, ClusterState, PortalState } from '../../api.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
// Phase 4.2 M4 patch2: student-facing [📖] Study drawer for the terminal UI.
import { StudyDrawer } from '../../StudyDrawer.js';
import { AttemptDots } from '../components/AttemptDots.js';
import { McqOptions } from '../components/McqOptions.js';
import { ShortTextInput } from '../components/ShortTextInput.js';
import { LongTextInput } from '../components/LongTextInput.js';
import { useKeyboard, type KeyHandler } from '../hooks/useKeyboard.js';
import { BattleAnimation } from '../components/BattleAnimation.js';
import { MAX_ATTEMPTS_DEFAULT, badgeName } from '../config.js';

type Mode =
  | { kind: 'cluster'; cluster: ClusterState; battleType: string }
  | { kind: 'portal'; portal: PortalState }
  // Phase 4.2 M6: practice mode. Posts to /api/practice/*; mode='PRACTICE'.
  // Eligibility (cluster cleared) is enforced server-side as defense-in-depth.
  | { kind: 'practice'; clusterId: string; clusterTitle: string }
  // Phase 4.2 M14: portal practice. Posts to /api/practice/portal/*; mode='PRACTICE'.
  // Eligibility (portal fully solved) is enforced server-side. NO exhaustion cap.
  | { kind: 'portal-practice'; portalKey: string; tier: number };

interface Props {
  mode: Mode;
  /**
   * Called once a non-retryable question terminus is reached (cleared, exhausted,
   * cluster_exhausted, or after Next-question fetch fails). Parent decides what
   * comes next: cluster-exit narrative, portal-exit narrative, or back-to-title.
   */
  onAdvance: (last?: { xpAwarded: number; newLevel: number; submit?: SubmitResp }) => void;
  onBackToTitle: () => void;
}

type Phase =
  | { name: 'loading' }
  | { name: 'error'; message: string }
  | { name: 'exhaustedCluster' }                       // 409 cluster_exhausted (no question)
  | { name: 'answering'; q: Question; attempt: number; maxAttempts: number }
  | { name: 'submitting'; q: Question; attempt: number; maxAttempts: number }
  | { name: 'feedback'; q: Question; resp: SubmitResp; canRetry: boolean };

const LONG_TEXT_CWS = new Set(['EXPLAIN', 'ANALYSE', 'ASSESS', 'DISCUSS', 'EVALUATE']);
const SHORT_TEXT_CWS = new Set(['DEFINE', 'STATE', 'CALCULATE']);

// Phase 4.2 M6: response shapes for practice endpoints. Defined locally
// because they're only consumed inside this screen (and avoid leaking M6 types
// into api.ts before the route stabilises).
interface PracticeHeadroomEnvelope {
  clusterId: string;
  maxXp: number;
  earnedXp: number;
  headroomXp: number;
}
interface PracticeNextResp {
  question: Question;
  mode: 'PRACTICE';
  cluster: PracticeHeadroomEnvelope;
  template: { maxXp: number; earnedXp: number; headroomXp: number };
  pasteEnabled?: boolean;
}
interface PracticeSubmitResp extends SubmitResp {
  cluster: PracticeHeadroomEnvelope;
}
// Phase 4.2 M14: portal-practice response shapes. The headroom envelope is
// keyed `portal` (portalKey/tier) rather than `cluster`.
interface PortalPracticeHeadroomEnvelope {
  portalKey: string;
  tier: number;
  maxXp: number;
  earnedXp: number;
  headroomXp: number;
}
interface PortalPracticeNextResp {
  question: Question;
  mode: 'PRACTICE';
  portal: PortalPracticeHeadroomEnvelope;
  template: { maxXp: number; earnedXp: number; headroomXp: number };
  pasteEnabled?: boolean;
}
interface PortalPracticeSubmitResp extends SubmitResp {
  portal: PortalPracticeHeadroomEnvelope;
}

function isMcq(q: Question): boolean {
  return Array.isArray(q.options) && q.options.length > 0;
}

function isLongText(q: Question): boolean {
  if (isMcq(q)) return false;
  const cw = (q.commandWord ?? '').toUpperCase();
  if (LONG_TEXT_CWS.has(cw)) return true;
  // Fallback: anything ≥ 4 marks treat as long-text
  return q.markValue >= 4;
}

function isShortText(q: Question): boolean {
  if (isMcq(q)) return false;
  if (isLongText(q)) return false;
  const cw = (q.commandWord ?? '').toUpperCase();
  if (SHORT_TEXT_CWS.has(cw)) return true;
  return true; // default everything else to short text
}

export function QuestionScreen({ mode, onAdvance, onBackToTitle }: Props) {
  const [phase, setPhase] = useState<Phase>({ name: 'loading' });
  const [answer, setAnswer] = useState('');
  // M10 — anti-paste knob (battle.paste_enabled). Defaults true; when the
  // server reports false on the /next response, paste into answer inputs is blocked.
  const [pasteEnabled, setPasteEnabled] = useState(true);
  // Phase 4.2 M4 patch2: Study drawer open state.
  const [studyOpen, setStudyOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // For the wrong-answer "re-render same question" line.
  const [retryNotice, setRetryNotice] = useState<string | null>(null);
  // Stable nonce to refocus inputs when we re-render the same question
  const rerenderKey = useRef(0);

  // Phase 4.2 M6: cluster-level headroom for practice mode (shown in banner +
  // refreshed after every submit). Null in cluster/portal mode.
  const [practiceHeadroom, setPracticeHeadroom] = useState<{
    maxXp: number;
    earnedXp: number;
    headroomXp: number;
  } | null>(null);

  // M14 — both practice flavours share submit/headroom plumbing. The portal
  // flavour posts to /api/practice/portal/submit and reads a `portal` envelope.
  const isPracticeMode = mode.kind === 'practice' || mode.kind === 'portal-practice';
  const submitUrl =
    mode.kind === 'portal-practice'
      ? '/api/practice/portal/submit'
      : mode.kind === 'practice'
        ? '/api/practice/submit'
        : '/api/battle/submit';

  // Pull the post-submit headroom envelope (cluster- or portal-keyed) off a
  // practice submit response and push it into the banner state.
  function applyPracticeHeadroom(resp: SubmitResp) {
    if (mode.kind === 'practice') {
      const p = resp as unknown as PracticeSubmitResp;
      if (p.cluster) {
        setPracticeHeadroom({ maxXp: p.cluster.maxXp, earnedXp: p.cluster.earnedXp, headroomXp: p.cluster.headroomXp });
      }
    } else if (mode.kind === 'portal-practice') {
      const p = resp as unknown as PortalPracticeSubmitResp;
      if (p.portal) {
        setPracticeHeadroom({ maxXp: p.portal.maxXp, earnedXp: p.portal.earnedXp, headroomXp: p.portal.headroomXp });
      }
    }
  }

  // ----- API fetchers ------------------------------------------------------
  async function fetchNext(retryTemplateId?: string) {
    setPhase({ name: 'loading' });
    setAnswer('');
    setSelected(null);
    try {
      if (mode.kind === 'cluster') {
        const qs = new URLSearchParams({ cluster: mode.cluster.clusterId, type: mode.battleType });
        if (retryTemplateId) qs.set('retry', retryTemplateId);
        const resp = await api.get<NextResp>(`/api/battle/next?${qs.toString()}`);
        setPasteEnabled(resp.pasteEnabled !== false);
        setPhase({
          name: 'answering',
          q: resp.question,
          attempt: resp.attemptNumber,
          maxAttempts: resp.maxAttempts ?? MAX_ATTEMPTS_DEFAULT,
        });
      } else if (mode.kind === 'portal') {
        const qs = new URLSearchParams();
        if (retryTemplateId) qs.set('retry', retryTemplateId);
        const url = `/api/portal/${encodeURIComponent(mode.portal.portalKey)}/next${
          qs.toString() ? `?${qs.toString()}` : ''
        }`;
        const resp = await api.get<PortalNextResp>(url);
        setPasteEnabled(resp.pasteEnabled !== false);
        setPhase({
          name: 'answering',
          q: resp.question,
          attempt: resp.attemptNumber,
          maxAttempts: resp.maxAttempts ?? MAX_ATTEMPTS_DEFAULT,
        });
      } else if (mode.kind === 'portal-practice') {
        // M14 — portal practice. NO exhaustion (server omits the MAX_ATTEMPTS
        // filter) so a 204 only means the portal genuinely has zero templates.
        const qs = new URLSearchParams({ portalKey: mode.portalKey });
        const resp = await fetch(`/api/practice/portal/next?${qs.toString()}`, {
          credentials: 'include',
        });
        if (resp.status === 204) {
          setPhase({ name: 'exhaustedCluster' });
          return;
        }
        if (!resp.ok) {
          throw new ApiError(resp.status, null, `portal_practice_next_failed (${resp.status})`);
        }
        const body = (await resp.json()) as PortalPracticeNextResp;
        setPasteEnabled(body.pasteEnabled !== false);
        setPracticeHeadroom({
          maxXp: body.portal.maxXp,
          earnedXp: body.portal.earnedXp,
          headroomXp: body.portal.headroomXp,
        });
        setPhase({
          name: 'answering',
          q: body.question,
          attempt: 1,
          maxAttempts: MAX_ATTEMPTS_DEFAULT,
        });
      } else {
        // mode.kind === 'practice'
        const qs = new URLSearchParams({ cluster: mode.clusterId });
        const resp = await fetch(`/api/practice/next?${qs.toString()}`, {
          credentials: 'include',
        });
        if (resp.status === 204) {
          // No active templates in this cluster — treat as exhausted.
          setPhase({ name: 'exhaustedCluster' });
          return;
        }
        if (!resp.ok) {
          throw new ApiError(resp.status, null, `practice_next_failed (${resp.status})`);
        }
        const body = (await resp.json()) as PracticeNextResp;
        setPasteEnabled(body.pasteEnabled !== false);
        setPracticeHeadroom({
          maxXp: body.cluster.maxXp,
          earnedXp: body.cluster.earnedXp,
          headroomXp: body.cluster.headroomXp,
        });
        setPhase({
          name: 'answering',
          q: body.question,
          // Practice has no "final attempt" gating; attempt counter is mostly cosmetic.
          attempt: 1,
          maxAttempts: MAX_ATTEMPTS_DEFAULT,
        });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          // Cluster exhausted — go to exhausted screen; parent will exit to title.
          setPhase({ name: 'exhaustedCluster' });
          return;
        }
        if (err.status === 404) {
          setPhase({ name: 'error', message: 'No questions available for this battle yet.' });
          return;
        }
      }
      setPhase({ name: 'error', message: 'Failed to load question. Please try again.' });
    }
  }

  // On mount and on mode change, fetch first question.
  useEffect(() => {
    fetchNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode.kind === 'cluster'
      ? mode.cluster.clusterId
      : mode.kind === 'portal'
        ? mode.portal.portalKey
        : mode.kind === 'portal-practice'
          ? mode.portalKey
          : mode.clusterId,
    mode.kind === 'cluster' ? mode.battleType : '',
  ]);

  async function submit() {
    if (phase.name !== 'answering') return;
    const q = phase.q;
    const raw = isMcq(q) ? (selected ?? '') : answer.trim();
    if (!raw) return;
    setPhase({ name: 'submitting', q, attempt: phase.attempt, maxAttempts: phase.maxAttempts });
    try {
      const resp = await api.post<SubmitResp>(submitUrl, {
        templateId: q.id,
        answer: raw,
        timeOnQuestionS: 30,
      });
      // Practice responses include post-submit headroom (cluster- or portal-keyed).
      if (isPracticeMode) applyPracticeHeadroom(resp);
      const canRetry = !resp.isFinalAttempt && !resp.gradeResult.cleared;
      setPhase({ name: 'feedback', q, resp, canRetry });
    } catch (err) {
      setPhase({ name: 'error', message: err instanceof ApiError ? err.message : 'Submission failed.' });
    }
  }

  function onRetry() {
    if (phase.name !== 'feedback') return;
    const q = phase.q;
    setRetryNotice('Wrong. -1 attempt.');
    rerenderKey.current += 1;
    fetchNext(q.id);
  }

  // Phase 4.2 M10l — cluster mode: a wrong answer rotates to a DIFFERENT uncleared
  // question (unpinned next). The server selector serves the least-recently-attempted
  // other uncleared question at the current mark tier (within-tier rotation); if this
  // is the only uncleared question at that tier it is re-served (ramp holds). No retry pin.
  function onClusterNext() {
    if (phase.name !== 'feedback') return;
    rerenderKey.current += 1;
    fetchNext(); // unpinned → rotate within-tier; never exits the battle
  }

  function onContinue() {
    if (phase.name !== 'feedback') {
      onAdvance();
      return;
    }
    const resp = phase.resp;
    // If portal mode and the answer cleared → fire onAdvance which the parent
    // routes to portal-exit narrative. Otherwise fetch next question.
    if (mode.kind === 'portal' && resp.gradeResult.cleared) {
      onAdvance({ xpAwarded: resp.xpAwarded, newLevel: resp.newLevel, submit: resp });
      return;
    }
    onAdvance({ xpAwarded: resp.xpAwarded, newLevel: resp.newLevel, submit: resp });
  }

  // Single "exhausted cluster" terminal state → push to advance immediately
  useEffect(() => {
    if (phase.name === 'exhaustedCluster') {
      // small delay so user sees the message
      const t = setTimeout(() => onAdvance(), 1200);
      return () => clearTimeout(t);
    }
  }, [phase.name, onAdvance]);

  // ----- Keyboard handlers per phase --------------------------------------
  const handlers: KeyHandler[] = useMemo(() => {
    const hs: KeyHandler[] = [];
    hs.push({ key: 'b', handler: onBackToTitle });
    if (phase.name === 'answering' && isMcq(phase.q)) {
      const opts = phase.q.options!;
      opts.forEach((opt, i) => {
        hs.push({
          key: String(i + 1),
          handler: () => {
            setSelected(opt.option);
            // Auto-submit on single-keystroke MCQ for snappy terminal feel
            setTimeout(() => {
              // grab fresh selected via state — using opt.option directly is fine
              if (phase.name === 'answering') {
                setSelected(opt.option);
                // Submit synchronously by reading state next tick
                void (async () => {
                  setPhase({ name: 'submitting', q: phase.q, attempt: phase.attempt, maxAttempts: phase.maxAttempts });
                  try {
                    const resp = await api.post<SubmitResp>(submitUrl, {
                      templateId: phase.q.id,
                      answer: opt.option,
                      timeOnQuestionS: 30,
                    });
                    if (isPracticeMode) applyPracticeHeadroom(resp);
                    const canRetry = !resp.isFinalAttempt && !resp.gradeResult.cleared;
                    setPhase({ name: 'feedback', q: phase.q, resp, canRetry });
                  } catch (err) {
                    setPhase({ name: 'error', message: err instanceof ApiError ? err.message : 'Submission failed.' });
                  }
                })();
              }
            }, 0);
          },
        });
      });
    }
    if (phase.name === 'feedback') {
      // Phase 4.2 M10l — cluster mode: cleared → advance (parent handles next/exit
      // narrative); wrong → rotate to a DIFFERENT uncleared question (no retry pin,
      // no [R] key). Portal/practice keep the existing canRetry/onRetry logic.
      if (mode.kind === 'cluster') {
        const forward = phase.resp.gradeResult.cleared ? onContinue : onClusterNext;
        hs.push({ key: 'Enter', handler: forward });
        // No [R] / retry key in cluster mode.
      } else {
        // Phase 4.1: single [Enter] action — retries when retry is available, otherwise advances.
        // [N] was a redundant alias and has been removed.
        hs.push({ key: 'Enter', handler: phase.canRetry ? onRetry : onContinue });
        if (phase.canRetry) {
          hs.push({ key: 'r', handler: onRetry });
        }
      }
    }
    // Phase 4.2 M4 patch2: [S] opens the Study drawer during answering/feedback when
    // the current question has at least one concept tagged with study content.
    if (
      (phase.name === 'answering' || phase.name === 'feedback') &&
      phase.q.hasStudyContent
    ) {
      hs.push({ key: 's', handler: () => setStudyOpen(true) });
    }
    if (phase.name === 'error') {
      hs.push({ key: 'Enter', handler: onBackToTitle });
    }
    return hs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, onBackToTitle]);

  useKeyboard(handlers);

  // ----- Render -----------------------------------------------------------
  const headerLabelNode =
    mode.kind === 'cluster'
      ? <>CLUSTER {mode.cluster.clusterId}</>
      : mode.kind === 'portal'
        ? <span className="tm-text--portal">PORTAL {mode.portal.endpointA} {'\u2194'} {mode.portal.endpointB}</span>
        : mode.kind === 'portal-practice'
          ? <span className="tm-feedback-correct">PRACTICE — Portal {mode.portalKey}</span>
          : <span className="tm-feedback-correct">PRACTICE — Cluster {mode.clusterId}</span>;

  if (phase.name === 'loading') {
    const title = (
      <>
        ECONQUEST <span className="tm-text--dim">{'\u2500'}</span> {headerLabelNode}
      </>
    );
    return (
      <TerminalFrame
        title={title}
        keybar={<KeyBar items={[{ label: 'B', description: 'back to title', onActivate: onBackToTitle }]} />}
      >
        <div className="tm-loading">{'>'} loading question…</div>
      </TerminalFrame>
    );
  }

  if (phase.name === 'exhaustedCluster') {
    const title = (
      <>
        ECONQUEST <span className="tm-text--dim">{'\u2500'}</span> {headerLabelNode} <span className="tm-text--dim">{'\u2500'}</span> <span className="tm-text--accent">COMPLETE</span>
      </>
    );
    return (
      <TerminalFrame
        title={title}
        keybar={<KeyBar items={[{ label: 'Enter', description: 'return to title', onActivate: onBackToTitle }]} />}
      >
        <div className="tm-section">
          <div className="tm-text tm-feedback-correct">{'>'} Every question in this {mode.kind} is cleared.</div>
        </div>
      </TerminalFrame>
    );
  }

  if (phase.name === 'error') {
    const title = (
      <>
        ECONQUEST <span className="tm-text--dim">{'\u2500'}</span> {headerLabelNode}
      </>
    );
    return (
      <TerminalFrame
        title={title}
        keybar={<KeyBar items={[{ label: 'Enter', description: 'back to title', onActivate: onBackToTitle }]} />}
      >
        <div className="tm-section">
          <div className="tm-text tm-text--warn">{'>'} {phase.message}</div>
        </div>
      </TerminalFrame>
    );
  }

  // ----- Answering / submitting / feedback phases -------------------------

  // Pull the active question for header (works for all three phases below)
  const activeQ: Question | null =
    phase.name === 'answering' || phase.name === 'submitting' || phase.name === 'feedback'
      ? phase.q
      : null;
  if (!activeQ) return null;

  const attempt: number =
    phase.name === 'feedback' ? phase.resp.attemptNumber : phase.attempt;
  const maxAttempts: number =
    phase.name === 'feedback' ? phase.resp.maxAttempts : phase.maxAttempts;
  const remaining = Math.max(0, maxAttempts - attempt + 1);

  const cw = (activeQ.commandWord ?? (isMcq(activeQ) ? 'MCQ' : '')).toUpperCase();
  const marks = activeQ.markValue;

  const titleMeta = (
    <>
      Q{activeQ.id.slice(0, 6)}{' '}
      <span className="tm-text--dim">{'\u00B7'}</span>{' '}
      {marks} mark{marks === 1 ? '' : 's'}{' '}
      <span className="tm-text--dim">{'\u00B7'}</span>{' '}
      {cw || 'QUESTION'}{' '}
      <span className="tm-text--dim">{'\u00B7'}</span>{' '}
      ATTEMPTS: <AttemptDots remaining={remaining} total={maxAttempts} />
    </>
  );

  const titleNode = (
    <>
      ECONQUEST <span className="tm-text--dim">{'\u2500'}</span> {headerLabelNode}
    </>
  );

  // Build keybar
  const keybar: KeyBarItem[] = [];
  if (phase.name === 'answering' && isMcq(activeQ)) {
    keybar.push({ label: '1-4', description: 'answer' });
  } else if (phase.name === 'answering' && isShortText(activeQ) && !isLongText(activeQ)) {
    keybar.push({ label: 'Enter', description: 'submit', onActivate: submit });
  } else if (phase.name === 'answering' && isLongText(activeQ)) {
    keybar.push({ label: 'Ctrl+Enter', description: 'submit', onActivate: submit });
  } else if (phase.name === 'submitting') {
    keybar.push({ label: '...', description: 'battling' });
  } else if (phase.name === 'feedback') {
    // Phase 4.2 M10l — cluster mode: cleared → [Enter] continue; wrong → [Enter]
    // next question (rotate). No [R] retry in cluster mode. Portal/practice unchanged.
    if (mode.kind === 'cluster') {
      const cleared = phase.resp.gradeResult.cleared;
      keybar.push({
        label: 'Enter',
        description: cleared ? 'continue' : 'next question',
        onActivate: cleared ? onContinue : onClusterNext,
      });
    } else if (phase.canRetry) {
      keybar.push({ label: 'R', description: 'retry', onActivate: onRetry });
      keybar.push({ label: 'Enter', description: 'retry', onActivate: onRetry });
    } else {
      keybar.push({ label: 'Enter', description: 'continue', onActivate: onContinue });
    }
    // Phase 4.1: [N] removed — [Enter] is the single "continue / next question" action.
  }
  // Phase 4.2 M4 patch2: [S] study button (disabled when no study content for any tagged concept).
  if (phase.name === 'answering' || phase.name === 'feedback') {
    const hasStudy = !!activeQ.hasStudyContent;
    keybar.push({
      label: 'S',
      description: hasStudy ? '📖 study' : '📖 no study',
      onActivate: hasStudy ? () => setStudyOpen(true) : undefined,
      disabled: !hasStudy,
    });
  }
  keybar.push({ label: 'B', description: 'back to title', onActivate: onBackToTitle });

  return (
    <TerminalFrame title={titleNode} meta={titleMeta} keybar={<KeyBar items={keybar} />}>
      {/* Phase 4.2 M6: practice banner — ALWAYS shown in practice mode (per Q3),
          even at 0 headroom, so students see their mastery state. */}
      {isPracticeMode && practiceHeadroom && (
        <div className="tm-section" style={{ marginBottom: '0.6rem' }}>
          <pre className="tm-text tm-feedback-correct" style={{ margin: 0, opacity: 0.85, whiteSpace: 'pre' }}>
{mode.kind === 'portal-practice'
  ? `╔═ PRACTICE MODE — Portal ${mode.portalKey} — ${practiceHeadroom.headroomXp} XP headroom ═╗`
  : `╔═ PRACTICE MODE — Cluster ${(mode as { clusterId: string }).clusterId} — ${practiceHeadroom.headroomXp} XP headroom ═╗`}
          </pre>
          <div className="tm-text tm-text--dim" style={{ marginTop: '0.2rem', fontSize: '0.85em' }}>
            {practiceHeadroom.headroomXp > 0
              ? `You've earned ${practiceHeadroom.earnedXp} / ${practiceHeadroom.maxXp} XP ${mode.kind === 'portal-practice' ? 'in this portal' : 'in this cluster'}. Keep practising to top up.`
              : `You've mastered this ${mode.kind === 'portal-practice' ? 'portal' : 'cluster'} (${practiceHeadroom.earnedXp} / ${practiceHeadroom.maxXp} XP). No more XP available — revise away.`}
          </div>
        </div>
      )}
      {/* Question stem */}
      <div className="tm-section">
        {retryNotice && phase.name === 'answering' && (
          <div className="tm-text tm-feedback-wrong" style={{ marginBottom: '0.6rem' }}>
            {'>'} {retryNotice}
          </div>
        )}
        <div className="tm-text" style={{ marginBottom: '0.6rem' }}>
          {activeQ.stem}
        </div>

        {/* Answer area only when answering or submitting (not on feedback) */}
        {(phase.name === 'answering' || phase.name === 'submitting') && (
          <div key={`q-${activeQ.id}-${rerenderKey.current}`}>
            {isMcq(activeQ) ? (
              <McqOptions
                options={activeQ.options!}
                disabled={phase.name === 'submitting'}
                onSelect={(o) => {
                  setSelected(o);
                  // Click-based submit (parity with key-press auto-submit)
                  if (phase.name === 'answering') {
                    void (async () => {
                      setPhase({ name: 'submitting', q: activeQ, attempt, maxAttempts });
                      try {
                        const resp = await api.post<SubmitResp>(submitUrl, {
                          templateId: activeQ.id,
                          answer: o,
                          timeOnQuestionS: 30,
                        });
                        if (isPracticeMode) applyPracticeHeadroom(resp);
                        const canRetry = !resp.isFinalAttempt && !resp.gradeResult.cleared;
                        setPhase({ name: 'feedback', q: activeQ, resp, canRetry });
                      } catch (err) {
                        setPhase({ name: 'error', message: err instanceof ApiError ? err.message : 'Submission failed.' });
                      }
                    })();
                  }
                }}
              />
            ) : isLongText(activeQ) ? (
              <LongTextInput
                value={answer}
                onChange={setAnswer}
                onSubmit={submit}
                disabled={phase.name === 'submitting'}
                rows={Math.max(4, Math.min(10, marks))}
                allowPaste={pasteEnabled}
              />
            ) : (
              <ShortTextInput
                value={answer}
                onChange={setAnswer}
                onSubmit={submit}
                disabled={phase.name === 'submitting'}
                allowPaste={pasteEnabled}
              />
            )}
            {phase.name === 'submitting' && (
              <BattleAnimation marks={phase.q.markValue} />
            )}
          </div>
        )}

        {/* Feedback */}
        {phase.name === 'feedback' && (
          <FeedbackPanel resp={phase.resp} alwaysShowModel={isPracticeMode} />
        )}
      </div>

      {/* Phase 4.2 M4 patch2: Study drawer overlay. Fixed-positioned via StudyDrawer's own CSS,
          so it renders above the terminal frame and is not affected by the tm-* layout. */}
      <StudyDrawer
        open={studyOpen}
        conceptCodes={activeQ.conceptTags ?? []}
        onClose={() => setStudyOpen(false)}
      />
    </TerminalFrame>
  );
}

function FeedbackPanel({ resp, alwaysShowModel = false }: { resp: SubmitResp; alwaysShowModel?: boolean }) {
  const r = resp.gradeResult;
  const correct = r.cleared;
  const lastAttempt = resp.isFinalAttempt && !r.cleared;
  // Phase 4.2 M6: in practice mode we ALWAYS reveal the model answer (no
  // anti-gaming concern — student has cleared the cluster).
  const showModel = alwaysShowModel || lastAttempt;
  return (
    <div className="tm-section">
      {correct ? (
        <div className="tm-feedback-correct">
          {'>'} CORRECT. +{resp.xpAwarded} XP
        </div>
      ) : lastAttempt ? (
        <div className="tm-feedback-wrong">{'>'} Out of attempts.</div>
      ) : (
        <div className="tm-feedback-wrong">{'>'} Wrong. -1 attempt.</div>
      )}
      <div className="tm-text" style={{ marginTop: '0.4rem' }}>
        Score: {r.score} / {r.marks}{' '}
        {r.marks > 0 && (
          <span className="tm-text--dim">({Math.round((r.score / r.marks) * 100)}%)</span>
        )}
      </div>
      {(r.ao1 != null || r.ao2 != null || r.ao3 != null || r.ao4 != null) && (
        <div className="tm-text tm-text--dim" style={{ marginTop: '0.3rem' }}>
          {r.ao1 != null && <>AO1: {r.ao1}{'  '}</>}
          {r.ao2 != null && <>AO2: {r.ao2}{'  '}</>}
          {r.ao3 != null && <>AO3: {r.ao3}{'  '}</>}
          {r.ao4 != null && <>AO4: {r.ao4}</>}
        </div>
      )}
      {r.feedback && (
        <div className="tm-feedback-body" style={{ marginTop: '0.6rem' }}>
          {r.feedback}
        </div>
      )}
      {r.modelAnswer && showModel && (
        <div className="tm-feedback-model">
          <div className="tm-text--accent" style={{ marginBottom: '0.3rem' }}>Model answer</div>
          {r.modelAnswer}
        </div>
      )}
      {resp.newBadges.length > 0 && (
        <div className="tm-text tm-text--accent" style={{ marginTop: '0.6rem' }}>
          {'\u2605'} Badge unlocked: {resp.newBadges.map((b) => badgeName(b)).join(', ')}
        </div>
      )}
    </div>
  );
}
